import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ACC_SCHEDULE_SEATS, UtilityService } from '@eforall/common';
import { ACC_APPLICATION_INTERVIEW_RATING_VALUE_MAP, AdmissionsData } from '@interfaces';
import { AppService, FuncService, TrimesterService } from '../../services';


@Injectable({ providedIn: 'root' })
export class AdmissionsAreaService {

	private func = inject(FuncService);
	public app = inject(AppService);
	private util = inject(UtilityService);
	private trimesterService = inject(TrimesterService);
	public readonly admissionsData = signal<AdmissionsData | undefined>(undefined);
	public readonly canEnrollEntrepreneurs = computed(() => this.app.data().permissions.canEnrollEntrepreneurs);
	public readonly canManageAdmissionsStaff = computed(() => this.app.data().permissions.canManageAdmissionsStaff);

	constructor() {
		toObservable(this.trimesterService.selectedTrimester).subscribe(async (selectedTrimester) => {
			if (selectedTrimester) await this.reloadData();
		});
	}

	public async reloadData() {
		const trimesterId = this.trimesterService.selectedTrimester()?.trimesterId;
		//Should never happen, but just in case.
		//This is a safety check to ensure that the admissions data is only loaded when a trimester is selected.
		//If no trimester is selected, the admissions data will not be loaded
		//and the service will not be able to provide any data.
		if (trimesterId) {
			const data = await this.func.admissions.getData({ trimesterId });

			const accProspectsMap = this.util.array.toMap(data.accProspects, p => p.accProspectId);

			this.admissionsData.set({
				...data,
				accelerators: data.accelerators.map(acc => ({
					...acc,
					availableSeats: data.accScheduleAndAvailableSeatCount[acc.accId]?.availableSeats ?? ACC_SCHEDULE_SEATS,
					seatsOffered: data.accScheduleAndAvailableSeatCount[acc.accId]?.seatsOffered ?? 0,
					seatsAccepted: data.accScheduleAndAvailableSeatCount[acc.accId]?.seatsAccepted ?? 0,
				})),
				accApplications: data.accApplications.map(appl => {
					const interview = data.accApplicationInterviews.find(interview => interview.accApplicationId == appl.accApplicationId);
					let interviewScore: number | undefined = undefined;
					if (interview) {
						const ratings = [interview.valuePropositionRating, interview.coachabilityRating, interview.sustainabilityRating, interview.timeCommitmentRating];
						interviewScore = this.util.score.calculateInterviewScore(ratings, ACC_APPLICATION_INTERVIEW_RATING_VALUE_MAP);
					}

					const sortingScoreMap = appl.accProspectId ? accProspectsMap[appl.accProspectId] : undefined;

					return {
						...appl,
						sortingScore: sortingScoreMap?.score ?? 0,
						rank: sortingScoreMap?.rank,
						interviewScore,
					}
				}),
			});
		}

	}

	public async loadAdmissionsData() {
		if (!this.admissionsData()) {
			await this.reloadData();
		}
	}

	async addEngagementIdToContactRequest(contactRequestId: number, engagementId: number) {
		await this.func.contactRequest.addEngagementId({ contactRequestId, engagementId });
	}

	public async updateAssignedStaffForApplication(accApplicationId: number, assignedToStaffId: number | undefined) {
		await this.func.accApplication.updateAccApplicationAssignedStaff({ accApplicationId, assignedToStaffId });
		await this.reloadData();
	}

	public async updateAssignedStaffForLead(accLeadId: number, assignedToStaffId: number | undefined) {
		await this.func.accLead.updateAccLeadAssignedStaff({ accLeadId, assignedToStaffId });
		await this.reloadData();
	}

}